<script lang="ts">
	import type { SvelteAsr, Mediator } from 'types/common'
	import type { Column, SortDirection } from '@isoftdata/svelte-table'
	import type { VendorSearchResult } from './results'
	import { getContext } from 'svelte'
	import { PersistedState } from 'runed'
	import { stringToBoolean } from '@isoftdata/utility-string'
	import { graphql, type MergeVendorsOptionsInput } from '$houdini'
	import { perPageCounts, type PerPageCount } from 'utility/search-fields'

	import BooleanIndicator from 'components/BooleanIndicator.svelte'
	import Button from '@isoftdata/svelte-button'
	import Checkbox from '@isoftdata/svelte-checkbox'
	import MergeEntityModal, { type Entity } from 'components/MergeEntityModal.svelte'
	import Select from '@isoftdata/svelte-select'
	import Table, { Td } from '@isoftdata/svelte-table'
	import { hasPermission } from 'utility/permission'

	import type { i18n } from 'types/common'
	const { t: translate } = getContext<i18n>('i18next')

	const mediator = getContext<Mediator>('mediator')
	const isVendorOptions = (options: Record<string, unknown>): options is MergeVendorsOptionsInput =>
		'purchaseOrders' in options

	interface Props {
		asr: SvelteAsr
		vendors: VendorSearchResult[]
		showActiveVendorsOnly: boolean
		perPageCount: PerPageCount
		currentPageNumber?: number
		totalItemsCount?: number | undefined
		orderByColumn: string | undefined
		orderByDirection: SortDirection | undefined
	}

	let {
		asr,
		vendors,
		showActiveVendorsOnly = true,
		perPageCount = $bindable(),
		currentPageNumber = 1,
		totalItemsCount = undefined,
		orderByColumn,
		orderByDirection,
	}: Props = $props()
	let mergeEntityModal: MergeEntityModal
	let selectedVendors = new PersistedState<Entity[]>('selectedVendors', [], {
		storage: 'session',
	})
	// TODO:
	// - add title on all the columns, could get it from the desktop

	const columns: Column<VendorSearchResult>[] = $derived([
		{
			name: '',
			property: 'isSelected',
			icon: 'check',
			align: 'center',
			sortType: false,
		},
		{
			name: translate('common:companyName', 'Company Name'),
			property: 'companyName',
			title: '',
		},
		{
			name: translate('common:contactName', 'Contact Name'),
			property: 'contactName',
			title: '',
		},
		{
			name: translate('vendorSearch.code', 'Code'),
			property: 'code',
			title: '',
			align: 'right',
		},
		{
			name: translate('common:phoneNumber', 'Phone Number'),
			property: 'phoneNumber',
			title: '',
			sortType: false,
		},
		{
			name: translate('common:faxNumber', 'Fax Number'),
			property: 'faxNumber',
			title: '',
			sortType: false,
		},
		{
			name: translate('common:mobileNumber', 'Mobile Number'),
			property: 'mobilePhoneNumber',
			title: '',
			sortType: false,
		},
		{
			name: translate('common:type', 'Type'),
			property: 'type',
			title: '',
		},
		{
			name: translate('common:number', 'Number'),
			property: 'id',
			title: '',
			align: 'right',
		},
		{
			name: translate('customer.dateEntered', 'Date Entered'),
			property: 'dateEntered',
			title: '',
			align: 'right',
		},
		{
			name: translate('common:streetAddress', 'Street Address'),
			property: 'billingAddress[address1]',
			title: '',
		},
		{
			name: translate('common:city', 'City'),
			property: 'billingAddress[city]',
			title: '',
		},
		{
			name: translate('common:state', 'State'),
			property: 'billingAddress[state]',
			title: '',
		},
		{
			name: translate('common:zip', 'Zip'),
			property: 'billingAddress[zip]',
			title: '',
			align: 'right',
		},
		{
			name: translate('common:country', 'Country'),
			property: 'billingAddress[country]',
			title: '',
		},
		{
			name: translate('common:emailAddress', 'Email Address'),
			property: 'email',
			title: '',
		},
		{
			name: translate('common:webAddress', 'Web Address'),
			property: 'webAddress',
			title: '',
		},
		{
			name: translate('common:active', 'Active'),
			property: 'active',
			align: 'center',
		},
		...getVendorCustomFieldColumns(vendors[0]),
	])

	function getVendorCustomFieldColumns(vendor: VendorSearchResult) {
		if (!vendor) {
			return []
		}
		const vendorOptionColumns: Column[] = []
		vendor.optionValues.forEach((optionValue, index) => {
			if (optionValue.option.showInList) {
				vendorOptionColumns.push({
					property: `optionValues[${index}][value]`,
					name: optionValue.option.name,
					align:
						(optionValue.option.type === 'CURRENCY' && 'right') ||
						(optionValue.option.type === 'BOOLEAN' && 'center') ||
						undefined,
					sortType: false,
				})
			}
		})
		return vendorOptionColumns
	}

	let sortColumn = $derived(columns.find(column => column.property === orderByColumn))

	function updateSelectedVendorIds(vendor: VendorSearchResult, isSelected: boolean) {
		if (isSelected) {
			selectedVendors.current.push({
				id: vendor.id,
				active: vendor.active,
				storeCode: null,
				companyName: vendor.companyName,
				contactName: vendor.contactName,
				email: vendor.email,
				phoneNumber: vendor.phoneNumber,
				streetAddress: vendor.billingAddress.address1,
			})
		} else {
			selectedVendors.current = selectedVendors.current.filter(selectedVendor => selectedVendor.id !== vendor.id)
		}
	}

	async function mergeVendor(
		masterVendorId: number,
		sourceVendorIds: number[],
		mergeOptions: MergeVendorsOptionsInput,
	) {
		const { data } = await mergeVendorMutation.mutate({
			input: {
				targetVendorId: masterVendorId.toString(),
				sourceVendorIds: sourceVendorIds.map(id => id.toString()),
				mergeOptions,
			},
		})

		if (!data) {
			throw new Error(translate('vendorSearch.failedToMergeVendors', 'Failed to merge vendors'))
		}

		selectedVendors.current = []
		mediator.publish('showMessage', {
			type: 'success',
			heading: translate('vendorSearch.mergedSuccessful!', 'Merged Successful!'),
			message: translate(
				'vendorSearch.vendorsHaveBeenMerged',
				'{{count}} vendors have been merged into vendor ID-{{masterVendorId}}',
				{ count: sourceVendorIds.length, masterVendorId },
			),
			time: 10,
		})
		asr.go(null, { lastSavedTime: new Date() }, { inherit: true })
	}

	const mergeVendorMutation = graphql(`
		mutation MergeVendors($input: MergeVendorsInput!) {
			mergeVendors(input: $input) {
				id
			}
		}
	`)
</script>

<div class="card-body">
	<div class="d-flex justify-content-between">
		<Checkbox
			inline
			label={translate('vendorSearch.showActiveVendorsOnly', 'Show Active Vendors Only')}
			checked={showActiveVendorsOnly}
			onchange={() => {
				asr.go(null, { showActiveVendorsOnly: !showActiveVendorsOnly }, { inherit: true })
			}}
		/>
		<Select
			label={translate('common:resultsPerPage', 'Results Per Page')}
			labelClass="mr-2"
			labelParentClass="form-inline mb-0"
			showEmptyOption={false}
			bind:value={perPageCount}
			onchange={() => {
				asr.go(null, { perPageCount, pageNumber: 1 }, { inherit: true })
			}}
		>
			{#each perPageCounts as perPageCount}
				<option value={perPageCount}>{perPageCount}</option>
			{/each}
		</Select>
	</div>
	<Table
		responsive
		stickyHeader
		columnHidingEnabled
		columnResizingEnabled
		{columns}
		{currentPageNumber}
		{totalItemsCount}
		{sortColumn}
		sortDirection={orderByDirection}
		rows={vendors}
		perPageCount={parseInt(perPageCount, 10)}
		parentStyle="max-height: 60vh"
		localStorageKey="vendor-search-results"
		columnClickedMethod={(column, direction) => {
			asr.go(null, { orderByColumn: column.property, orderByDirection: direction }, { inherit: true })
			return Promise.resolve()
		}}
		pageChange={({ pageNumber }) => asr.go(null, { pageNumber }, { inherit: true })}
	>
		{#snippet noRows({ visibleColumnsCount })}
			<tr>
				<td
					colspan={visibleColumnsCount}
					class="text-center">{translate('vendorSearch.noVendorsFound', 'No vendors found')}</td
				>
			</tr>
		{/snippet}
		{#snippet children({ row: vendor })}
			{@const isSelected = selectedVendors.current.find(selectedVendor => selectedVendor.id === vendor.id)}
			<tr
				class="cursor-pointer"
				class:text-muted={!vendor.active}
				onclick={() => asr.go('app.vendor', { vendorId: vendor.id })}
			>
				<Td
					property="isSelected"
					onclick={event => {
						event.stopPropagation()
						if (event.target && (event.target as HTMLElement).id !== `vendor-${vendor.id}`) {
							updateSelectedVendorIds(vendor, !isSelected)
						}
					}}
				>
					<Checkbox
						id={`vendor-${vendor.id}`}
						showLabel={false}
						checked={!!isSelected}
						onchange={() => {
							updateSelectedVendorIds(vendor, !isSelected)
						}}
					/>
				</Td>
				<Td property="companyName">{vendor.companyName}</Td>
				<Td property="contactName">{vendor.contactName}</Td>
				<Td property="code">{vendor.code}</Td>
				<Td
					property="phoneNumber"
					class="text-nowrap"
				>
					{vendor.phoneNumber}
				</Td>
				<Td
					property="faxNumber"
					class="text-nowrap"
				>
					{vendor.faxNumber}
				</Td>
				<Td property="mobilePhoneNumber">{vendor.mobilePhoneNumber}</Td>
				<Td property="type">{vendor.type}</Td>
				<Td property="id">{vendor.id}</Td>
				<Td property="dateEntered">{vendor.dateEntered ? vendor.dateEntered.toLocaleDateString() : 'N/A'}</Td>
				<Td property="billingAddress[address1]">{vendor.billingAddress.address1}</Td>
				<Td property="billingAddress[city]">{vendor.billingAddress.city}</Td>
				<Td property="billingAddress[state]">{vendor.billingAddress.state}</Td>
				<Td property="billingAddress[zip]">{vendor.billingAddress.zip}</Td>
				<Td property="billingAddress[country]">{vendor.billingAddress.country}</Td>
				<Td property="email">{vendor.email}</Td>
				<Td property="webAddress">{vendor.webAddress}</Td>
				<Td property="active">
					<BooleanIndicator value={vendor.active} />
				</Td>
				{#each vendor.optionValues as optionValue, index}
					{#if optionValue.option.showInList}
						<Td
							property={`optionValues[${index}][value]`}
							class={optionValue.option.type === 'CURRENCY' ? 'text-right' : ''}
						>
							{#if optionValue.option.type === 'DATE'}
								{optionValue.value ? new Date(optionValue.value).toLocaleDateString() : ''}
							{:else if optionValue.option.type === 'BOOLEAN'}
								<BooleanIndicator value={stringToBoolean(optionValue.value)} />
							{:else}
								{optionValue.value}
							{/if}
						</Td>
					{/if}
				{/each}
			</tr>
		{/snippet}
	</Table>
</div>

<div class="card-footer d-flex justify-content-between align-items-center">
	<div>
		<Button
			outline
			size="sm"
			iconClass="merge"
			disabled={!hasPermission('VENDORS_CAN_MERGE') || selectedVendors.current.length < 2}
			onclick={() => mergeEntityModal.open(selectedVendors.current)}
		>
			{translate('vendorSearch.mergeVendors', 'Merge Vendors')}
		</Button>
		{#if selectedVendors.current.length > 0}
			<Button
				outline
				size="sm"
				color="warning"
				iconClass="broom"
				onclick={() => {
					selectedVendors.current = []
				}}
			>
				{translate('common:clearSelection', 'Clear Selection')}
			</Button>
		{/if}
	</div>
	<span>{totalItemsCount ? `${totalItemsCount} Vendors Found` : 'No vendors found'}</span>
</div>

<MergeEntityModal
	bind:this={mergeEntityModal}
	entityType="vendor"
	title={translate('vendorSearch.mergeVendors', 'Merge Vendors')}
	mergeFn={async (masterVendorId, sourceVendorIds, mergeOptions) => {
		if (isVendorOptions(mergeOptions)) {
			await mergeVendor(masterVendorId, sourceVendorIds, mergeOptions)
		}
	}}
/>

<style>
	* :global(.text-dark-goldenrod) {
		color: darkgoldenrod;
	}
</style>
